import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { listenToOrderStatus } from '@/lib/orderStatusChannel';
import { useRefetchOnResume } from '@/hooks/useRefetchOnResume';
import { preorderStorageKey } from '@/lib/preorder';
import type { CollectionPoint, MenuItem } from '@/lib/supabase-types';
import type { TranslateFn } from '@/hooks/useTranslation';

// A fan can hold a half-time pre-order AND a normal "now" order at the same
// time on TagPage: a pre-order paid during the first half and collected at
// half-time, plus a separate in-play order for right now. Folding both into
// TagPage's own checkPendingOrder/order_updates_<id>/useRefetchOnResume trio
// would mean every "now" order branch also has to reason about pickup_slot
// and the pre-order's own terminal states. Instead this hook owns the
// pre-order's lifecycle end to end, modelled on that same machinery but
// fully self-contained — TagPage's now-order state and code paths are not
// touched here; Tasks 8-9 wire this hook in alongside them.

/** How long a `pending_payment` pre-order is given before it's abandoned. */
const ABANDON_THRESHOLD_MS = 30 * 60 * 1000;

/**
 * Statuses for which the pre-order is still moving toward collection. Also
 * the only statuses that hold a realtime subscription open — a terminal
 * status (cancelled/collected/refunded) has nothing left to change, and
 * subscribing anyway is how a stray SUBSCRIBED (on mount, or after a socket
 * rejoin) used to trigger a silent refresh that wiped the very state a
 * detail screen needs to explain the outcome.
 */
const MOVING_STATUSES = ['pending_payment', 'scheduled', 'pending', 'ready_at_collection_point'];

/**
 * How long after a CLIENT-confirmed payment (Stripe's confirmPayment returned
 * `succeeded`) a `cancelled` row is treated as a stale abandon rather than a
 * real cancellation. A card declined once and then retried inside the same
 * PaymentSheet (same PaymentIntent) leaves the order cancelled/failed — by
 * onError's abandon-order and by Stripe's payment_failed — until the
 * webhook's `succeeded` revives it (seen ~50 s later on staging). The revive
 * is guaranteed server-side, so within this window the tracker keeps its
 * state, pointer and channel and waits for that UPDATE. Generous on purpose:
 * webhooks retry; after the window a cancel is accepted as usual.
 */
export const CANCEL_GRACE_MS = 5 * 60_000;

/**
 * True when an incoming status for `orderId` is a stale abandon of an order
 * the client has just seen paid: same order, still inside the grace window
 * since the confirmed payment, `cancelled`, and not a refund (a refund is a
 * real, deliberate outcome and never provisional).
 */
export function isStaleCancel(
  orderId: string,
  incoming: { status?: string | null; payment_status?: string | null },
  tracked: { id: string; confirmedPaidAt: number | null } | null | undefined,
): boolean {
  if (!tracked || tracked.id !== orderId || tracked.confirmedPaidAt == null) return false;
  if (Date.now() - tracked.confirmedPaidAt >= CANCEL_GRACE_MS) return false;
  return incoming.status === 'cancelled' && incoming.payment_status !== 'refunded';
}

/**
 * The confirmed-paid stamp is persisted next to an order pointer (pre-order
 * `idseat_preorder_<tag>` or now-order `idseat_pending_order_<tag>`), so a
 * reload in the seconds between confirmPayment → succeeded and the webhook's
 * revive still knows the payment was confirmed. It is inert for any other
 * order id and after CANCEL_GRACE_MS, so correctness never depends on
 * clearing it.
 */
export function confirmedPaidStorageKey(pointerKey: string): string {
  return `${pointerKey}:paidAt`;
}

export function writeConfirmedPaidAt(pointerKey: string, orderId: string, paidAt: number = Date.now()): void {
  localStorage.setItem(confirmedPaidStorageKey(pointerKey), JSON.stringify({ id: orderId, paidAt }));
}

/** The stored stamp for `orderId`, or null (none, another order, malformed). */
export function readConfirmedPaidAt(pointerKey: string, orderId: string): number | null {
  try {
    const raw = localStorage.getItem(confirmedPaidStorageKey(pointerKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown; paidAt?: unknown } | null;
    if (!parsed || parsed.id !== orderId || typeof parsed.paidAt !== 'number') return null;
    return parsed.paidAt;
  } catch {
    return null;
  }
}

export function clearConfirmedPaidAt(pointerKey: string): void {
  localStorage.removeItem(confirmedPaidStorageKey(pointerKey));
}

export interface PreorderItem {
  menu_item_id?: string;
  item_name: string;
  item_size: string | null;
  item_price: number;
  quantity: number;
  selected_addons?: { name: string; price: number }[];
  item_vat_rate?: number;
}

export interface TrackedPreorder {
  id: string;
  /** pending_payment | scheduled | pending | ready_at_collection_point | collected | cancelled */
  status: string;
  paymentStatus: string | null;
  total: number;
  items: PreorderItem[];
  collectionPoint: CollectionPoint | null;
  createdAt: string | null;
  /** Pick-up number, assigned per club per match day (orders.pickup_code); null when unknown. */
  pickupCode: string | null;
  /**
   * Date.now() when remember() recorded a client-confirmed payment for this
   * order (restored from its persisted stamp after a reload); null for a
   * state loaded from the server without one. Drives isStaleCancel.
   */
  confirmedPaidAt: number | null;
}

export interface PreorderSnapshot {
  items: PreorderItem[];
  total: number;
  collectionPoint: CollectionPoint | null;
  /** The code create-payment-intent returned, when it had one. */
  pickupCode?: string | null;
}

export interface UseHalfTimePreorder {
  preorder: TrackedPreorder | null;
  /** Non-silent refresh in flight (safe to show a loader for). */
  checking: boolean;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  /** Removes the pointer and the state. */
  clear: () => void;
  remember: (orderId: string, snapshot: PreorderSnapshot) => void;
}

export function useHalfTimePreorder({
  tagId,
  menuItems,
  t,
}: {
  tagId: string | null | undefined;
  menuItems: MenuItem[];
  t: TranslateFn;
}): UseHalfTimePreorder {
  const [preorder, setPreorder] = useState<TrackedPreorder | null>(null);
  const [checking, setChecking] = useState(false);

  // "Latest value" refs so callbacks that intentionally have a narrow
  // dependency list (the realtime effect below re-subscribes only when the
  // tracked order's id or moving-ness changes; refresh below reads the
  // current snapshot without depending on it) never read a stale
  // tagId/menuItems/t/preorder from the render they were created in.
  const tagIdRef = useRef(tagId);
  tagIdRef.current = tagId;
  const menuItemsRef = useRef(menuItems);
  menuItemsRef.current = menuItems;
  const tRef = useRef(t);
  tRef.current = t;
  const preorderRef = useRef<TrackedPreorder | null>(null);
  preorderRef.current = preorder;
  // Order id a stale cancel was last logged for — log it once, not on every
  // 30 s poll inside the grace window.
  const staleCancelLoggedRef = useRef<string | null>(null);

  const fetchItems = useCallback(async (orderId: string): Promise<PreorderItem[]> => {
    const { data } = await supabase.rpc('get_order_items', { _order_id: orderId });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data || []).map((it: any) => {
      const resolvedId = it.menu_item_id || menuItemsRef.current.find((mi) => mi.name === it.item_name)?.id;
      return { ...it, menu_item_id: resolvedId, item_vat_rate: Number(it.item_vat_rate) || 0 };
    });
  }, []);

  const clear = useCallback(() => {
    if (tagId) {
      localStorage.removeItem(preorderStorageKey(tagId));
      clearConfirmedPaidAt(preorderStorageKey(tagId));
    }
    setPreorder(null);
  }, [tagId]);

  const refresh = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!tagId) return;
      const storageKey = preorderStorageKey(tagId);
      const savedOrderId = localStorage.getItem(storageKey);
      if (!savedOrderId) {
        // The mount/tagId effect owns "no pointer → null"; a refresh
        // invoked later (resume poll, realtime resubscribe-on-SUBSCRIBED)
        // with no pointer left to check has nothing to do here — touching
        // state would wipe a terminal snapshot a detail screen is still
        // showing.
        return;
      }

      const silent = opts?.silent ?? false;
      if (!silent) setChecking(true);
      try {
        const { data, error } = await supabase.rpc('get_order_status', { _order_id: savedOrderId });
        if (error) throw error;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const row = data?.[0] as any;
        // An older get_order_status shape has no pickup_code column: keep the
        // code remember() stored for this same order rather than wiping it.
        const codeFor = (r: { id: string; pickup_code?: string | null }): string | null =>
          r.pickup_code ?? (preorderRef.current?.id === r.id ? preorderRef.current.pickupCode : null) ?? null;
        // Carry remember()'s confirmed-payment stamp across refreshes of the
        // same order, so the grace window runs from the payment, not from
        // whichever refresh happened last. After a reload the state is gone,
        // so fall back to the stamp remember() persisted next to the pointer.
        const paidAtFor = (r: { id: string }): number | null =>
          (preorderRef.current?.id === r.id ? preorderRef.current.confirmedPaidAt : null) ??
          readConfirmedPaidAt(storageKey, r.id);
        if (!row) {
          clear();
          return;
        }

        if (row.status === 'pending_payment') {
          const current = preorderRef.current;
          if (current && current.id === row.id && current.status === 'scheduled') {
            // remember() already set the optimistic 'scheduled'/paid
            // snapshot for this same order the moment Stripe confirmed the
            // PaymentIntent. The webhook hasn't landed yet, so a row that
            // still reads pending_payment here is stale, not a demotion —
            // overwriting would flash "processing payment" right after a
            // successful payment.
            return;
          }
          // Mirrors TagPage's checkPendingOrder: the webhook can lag a few
          // seconds behind the fan returning to the page, so don't abandon
          // immediately — only once it's genuinely stale.
          const ageMs = Date.now() - new Date(row.created_at).getTime();
          if (ageMs < ABANDON_THRESHOLD_MS) {
            const items = await fetchItems(savedOrderId);
            setPreorder({
              id: row.id,
              status: row.status,
              paymentStatus: row.payment_status ?? null,
              total: Number(row.total_amount),
              items,
              collectionPoint: row.collection_point ?? null,
              createdAt: row.created_at ?? null,
              pickupCode: codeFor(row),
              confirmedPaidAt: paidAtFor(row),
            });
          } else {
            supabase.functions
              .invoke('abandon-order', { body: { order_id: savedOrderId, tag_id: tagId } })
              .catch(console.warn);
            clear();
          }
          return;
        }

        if (isStaleCancel(row.id, row, { id: row.id, confirmedPaidAt: paidAtFor(row) })) {
          // A cancel read right after the client saw this payment succeed is
          // the declined-then-retried abandon, not the outcome: keep state and
          // pointer (so the channel stays open) until the webhook's revive
          // UPDATE lands. See CANCEL_GRACE_MS.
          if (staleCancelLoggedRef.current !== row.id) {
            staleCancelLoggedRef.current = row.id;
            console.info('[useHalfTimePreorder] ignoring a stale cancel right after a confirmed payment:', row.id);
          }
          if (preorderRef.current?.id === row.id) return;
          // Reload during the webhook lag: no state held for this order yet.
          // Show the same optimistic paid snapshot remember() sets, so the
          // order is "moving" — the channel opens and the poll runs until the
          // revive UPDATE (or the grace expiring) resolves it.
          const items = await fetchItems(savedOrderId);
          setPreorder({
            id: row.id,
            status: 'scheduled',
            paymentStatus: 'paid',
            total: Number(row.total_amount),
            items,
            collectionPoint: row.collection_point ?? null,
            createdAt: row.created_at ?? null,
            pickupCode: codeFor(row),
            confirmedPaidAt: paidAtFor(row),
          });
          return;
        }

        if (row.status === 'cancelled' || row.status === 'collected' || row.payment_status === 'refunded') {
          // Terminal state — let a detail screen explain it once, then stop
          // treating it as "the" tracked pre-order.
          localStorage.removeItem(storageKey);
          clearConfirmedPaidAt(storageKey);
          setPreorder({
            id: row.id,
            status: row.status,
            paymentStatus: row.payment_status ?? null,
            total: Number(row.total_amount),
            items: [],
            collectionPoint: row.collection_point ?? null,
            createdAt: row.created_at ?? null,
            pickupCode: codeFor(row),
            confirmedPaidAt: paidAtFor(row),
          });
          return;
        }

        // scheduled | pending | ready_at_collection_point
        const items = await fetchItems(savedOrderId);
        setPreorder({
          id: row.id,
          status: row.status,
          paymentStatus: row.payment_status ?? null,
          total: Number(row.total_amount),
          items,
          collectionPoint: row.collection_point ?? null,
          createdAt: row.created_at ?? null,
          pickupCode: codeFor(row),
          confirmedPaidAt: paidAtFor(row),
        });
      } catch (err) {
        // A transient network/RPC error must not drop a paid pre-order: this
        // runs on every focus/visibility/online event and every 30s, and
        // 'online' fires exactly when the stadium network is flaky. Log and
        // keep the pointer + whatever state is already held; the next
        // successful refresh reconciles it. Clearing stays reserved for a
        // definitive not-found row, a stale pending_payment, and the
        // terminal statuses above.
        console.error('[useHalfTimePreorder] refresh failed:', err);
      } finally {
        if (!silent) setChecking(false);
      }
    },
    [tagId, clear, fetchItems],
  );

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const remember = useCallback(
    (orderId: string, snapshot: PreorderSnapshot) => {
      if (!tagId) return;
      // The Stripe success callback that calls this fires only once the
      // PaymentIntent has actually succeeded; the webhook's realtime UPDATE
      // will separately confirm/advance the status.
      const paidAt = Date.now();
      localStorage.setItem(preorderStorageKey(tagId), orderId);
      writeConfirmedPaidAt(preorderStorageKey(tagId), orderId, paidAt);
      setPreorder({
        id: orderId,
        status: 'scheduled',
        paymentStatus: 'paid',
        total: snapshot.total,
        items: snapshot.items,
        collectionPoint: snapshot.collectionPoint,
        createdAt: new Date().toISOString(),
        pickupCode: snapshot.pickupCode ?? null,
        confirmedPaidAt: paidAt,
      });
    },
    [tagId],
  );

  // Mount / tagId change: pick up an already-tracked pre-order.
  useEffect(() => {
    if (!tagId || !localStorage.getItem(preorderStorageKey(tagId))) {
      setPreorder(null);
      return;
    }
    refresh();
  }, [tagId, refresh]);

  // Realtime updates for the tracked pre-order — held open only while it is
  // still moving (the same MOVING_STATUSES the resume refetch below uses).
  // A terminal status therefore never subscribes (nothing will change), and
  // a status that flips to terminal while a channel is open tears it down
  // on the next render instead of leaving a stray subscription whose next
  // SUBSCRIBED (mount, or a socket rejoin) would otherwise silently refresh
  // and wipe the terminal snapshot. Re-subscribes only when the id or the
  // moving-ness changes — not on every status flip within the moving set,
  // and not when tagId/t change identity — so tagId/t/the previous status
  // are all read from refs kept fresh every render instead of this effect's
  // own (otherwise stale) closure.
  const id = preorder?.id;
  const moving = !!preorder && MOVING_STATUSES.includes(preorder.status);
  useEffect(() => {
    if (!id || !moving) return;

    // The order's own broadcast topic (order:<id>): fans cannot read the
    // orders table, the database pushes each status change there.
    const channel = listenToOrderStatus(supabase, id,
        (updated) => {
          // The declined-then-retried abandon (or Stripe's payment_failed)
          // arriving after the client saw the payment succeed: ignore it; the
          // webhook's revive UPDATE follows on this same channel.
          if (isStaleCancel(id, updated, preorderRef.current)) return;
          const previous = preorderRef.current;
          const previousStatus = previous?.status ?? null;
          const previousPaymentStatus = previous?.paymentStatus ?? null;
          // Advance the ref now as well as the state: a second payload can
          // arrive before the next render refreshes the ref, and must compare
          // against this one, not the one before it.
          if (previous) {
            preorderRef.current = {
              ...previous,
              status: updated.status,
              paymentStatus: updated.payment_status ?? previous.paymentStatus,
            };
          }

          setPreorder((prev) =>
            prev
              ? { ...prev, status: updated.status, paymentStatus: updated.payment_status ?? prev.paymentStatus }
              : prev,
          );

          const removePointer = () => {
            if (!tagIdRef.current) return;
            localStorage.removeItem(preorderStorageKey(tagIdRef.current));
            clearConfirmedPaidAt(preorderStorageKey(tagIdRef.current));
          };

          if (updated.payment_status === 'refunded') {
            // A full refund of a scheduled pre-order arrives as two UPDATEs
            // (payment_status, then status → cancelled), both carrying
            // 'refunded' — announce it once.
            if (previousPaymentStatus !== 'refunded') {
              removePointer();
              toast.info(tRef.current('payment.orderRefunded', { defaultValue: 'Your order has been refunded.' }));
            }
          } else if (updated.status === 'cancelled') {
            removePointer();
            toast.error(tRef.current('payment.orderCancelled', { defaultValue: 'Your order has been cancelled.' }));
          } else if (updated.status === 'collected') {
            removePointer();
            toast.success(tRef.current('ordering.orderCollected'));
          } else if (updated.status === 'ready_at_collection_point') {
            toast.success(tRef.current('ordering.orderReadyAtCollection'));
          } else if (updated.status === 'pending' && previousStatus === 'scheduled') {
            toast.success(tRef.current('preorder.preparingToast'));
          }
        },
      )
      .subscribe((status) => {
        // A (re)subscribe after a dropped socket replays nothing that fired
        // in the gap, so re-read the authoritative status.
        if (status === 'SUBSCRIBED') refreshRef.current({ silent: true });
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, moving]);

  useRefetchOnResume(() => refresh({ silent: true }), { enabled: moving, intervalMs: 30_000 });

  return { preorder, checking, refresh, clear, remember };
}
